// Import all wallet provider factories and types
//
// IMPORTANT: only `createEVMProvider`, `createImmutablePassportProvider`, and
// `imtblPassportLoginCallback` are value-imported eagerly from the chain
// packages. Every other chain is wired via a descriptor (see
// ./descriptors/*) so the chain SDK stays out of the host's entry chunk
// until the user actually opens the connect modal. Direct consumers can
// still `import { create<X>Provider } from "@layerswap/wallet-<chain>"`
// themselves if they want the eager path.
import { createBitcoinDescriptor } from "./descriptors/bitcoin";
import { createEVMProvider } from "@layerswap/wallet-evm";
import { createFuelDescriptor } from "./descriptors/fuel";
import { createImmutablePassportDescriptor } from "./descriptors/imtblPassport";
import { createParadexDescriptor } from "./descriptors/paradex";
import { createStarknetDescriptor } from "./descriptors/starknet";
import { createSVMDescriptor } from "./descriptors/svm";
import { createTONDescriptor } from "./descriptors/ton";
import { createTronDescriptor } from "./descriptors/tron";
export { defineWalletDescriptor } from "./descriptors/defineWalletDescriptor";
export { createBitcoinDescriptor };
export { createEVMProvider };
export { createFuelDescriptor };
export { createImmutablePassportDescriptor };
// For eager `createImmutablePassportProvider` / `imtblPassportLoginCallback`
// see the dedicated subpath: `@layerswap/wallets/eager/imtbl-passport`.
export { createParadexDescriptor };
export { createStarknetDescriptor };
export { createSVMDescriptor };
export { createTONDescriptor };
export { createTronDescriptor };
/**
 * Creates and returns a default configuration of all wallet providers.
 *
 * Only EVM (and Immutable Passport, when configured) is wired eagerly. Every
 * other chain ships as a `WalletProviderDescriptor` — the real SDK is
 * dynamic-imported on first connect-modal open. This keeps starknet,
 * @paradex/sdk, @ton/*, @tonconnect/sdk, @fuel-ts/*, @solana/web3.js,
 * tronweb (+ its transitive `validator`/`bignumber.js`), bitcoinjs-lib,
 * @bigmi, and the connector adapters out of the host's entry chunk.
 *
 * @param config - Configuration options for the wallet providers
 * @returns Array of configured wallet providers ready to be passed to LayerswapProvider
 *
 * @example
 * ```tsx
 * import { getDefaultProviders } from "@layerswap/wallets";
 * import { LayerswapProvider, Swap } from "@layerswap/widget";
 *
 * const walletProviders = getDefaultProviders({
 *   walletConnect: {
 *     projectId: 'your-project-id',
 *     name: 'Your App',
 *     description: 'Your App Description',
 *     url: 'https://yourapp.com',
 *     icons: ['https://yourapp.com/icon.png']
 *   },
 *   ton: {
 *     tonApiKey: 'your-ton-api-key',
 *     manifestUrl: 'https://yourapp.com/tonconnect-manifest.json'
 *   }
 * });
 *
 * export default function App() {
 *   return (
 *     <LayerswapProvider walletProviders={walletProviders}>
 *       <Swap />
 *     </LayerswapProvider>
 *   );
 * }
 * ```
 */
export function getDefaultProviders(config = {}) {
    const { walletConnect, ton, immutablePassport } = config;
    const providers = [
        // EVM — eager (common case, kept on the initial bundle).
        createEVMProvider({
            walletConnectConfigs: walletConnect,
        }),
        // Starknet — lazy. Pulls starknet/starkware-crypto on connect.
        createStarknetDescriptor(),
        // Fuel — lazy. Pulls @fuel-ts/* + @fuels/vm-asm on connect.
        createFuelDescriptor(),
        // Paradex — lazy. Drags @paradex/sdk → starknet → starkware-crypto.
        createParadexDescriptor(),
        // Bitcoin — lazy. Pulls bitcoinjs-lib + @bigmi + bn.js + tweetnacl.
        createBitcoinDescriptor(),
        // TON — lazy and conditional (only included when tonConfigs supplied,
        // matching the prior eager behaviour).
        ...(ton ? [createTONDescriptor(ton)] : []),
        // SVM (Solana) — lazy. Pulls @solana/web3.js + SolanaWalletConnectAdapter.
        createSVMDescriptor(walletConnect),
        // Tron — lazy. Pulls tronweb + its transitive validator/bignumber/protobuf.
        createTronDescriptor(),
        // Immutable Passport — lazy and conditional. The SDK + service
        // init chain pulls ~993 KB Brotli; deferring it to first
        // connect-modal open removes that from the home page waterfall.
        // The OAuth redirect page (`/imtblRedirect`) imports the
        // eager factory + login callback directly so it still works.
        ...(immutablePassport ? [createImmutablePassportDescriptor(immutablePassport)] : [])
    ];
    return providers;
}
